import React from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../lib/AuthContext";
import { useI18n } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { AUTH } from "../constants/testIds";

export default function Login() {
    const { login, verifyMfa } = useAuth();
    const { t } = useI18n();
    const navigate = useNavigate();
    const { state } = useLocation();
    const [email, setEmail] = React.useState("");
    const [password, setPassword] = React.useState("");
    const [loading, setLoading] = React.useState(false);
    // MFA challenge state — set after step 1 when the account has 2FA on.
    const [challenge, setChallenge] = React.useState(null);
    const [code, setCode] = React.useState("");

    function dashPathFor(role) {
        if (role === "admin" || role === "superadmin") return "/admin";
        if (role === "teacher") return "/teacher";
        return "/dashboard";
    }

    async function onSubmit(e) {
        e.preventDefault();
        setLoading(true);
        try {
            const res = await login(email, password);
            if (res?.mfa_required) {
                setChallenge(res.challenge);
                toast.info("Enter the 6-digit code from your authenticator app");
                return;
            }
            toast.success(`Welcome back, ${res.full_name}`);
            navigate(state?.from || dashPathFor(res.role));
        } catch (err) {
            toast.error(err.response?.data?.detail || "Login failed");
        } finally { setLoading(false); }
    }

    async function onVerify(e) {
        e.preventDefault();
        setLoading(true);
        try {
            const u = await verifyMfa(challenge, code);
            toast.success(`Welcome back, ${u.full_name}`);
            navigate(state?.from || dashPathFor(u.role));
        } catch (err) {
            toast.error(err.response?.data?.detail || "Verification failed");
        } finally { setLoading(false); }
    }

    return (
        <main className="min-h-[calc(100vh-5rem)] flex items-center py-16">
            <div className="max-w-5xl mx-auto px-6 lg:px-10 grid md:grid-cols-2 gap-12 items-center w-full">
                <div className="hidden md:block">
                    <div className="overline text-brand-stone mb-4">{t("login")}</div>
                    <h1 className="font-serif text-5xl leading-tight text-brand-ink">
                        {t("login_title")} <br /><span className="italic text-primary">As-salamu alaykum.</span>
                    </h1>
                    <p className="mt-6 text-brand-stone max-w-md">
                        {t("login_subtitle")}
                    </p>
                </div>
                <Card className="border-border">
                    <CardContent className="p-8">
                        {!challenge ? (
                        <form onSubmit={onSubmit} className="space-y-5">
                            <div className="mb-4">
                                <div className="overline text-brand-stone">Islamic Center of Federal Way</div>
                                <h2 className="font-serif text-2xl mt-1 text-brand-ink">{t("login_title")}</h2>
                            </div>
                            <div>
                                <Label htmlFor="email">{t("email")}</Label>
                                <Input id="email" type="email" required value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    data-testid={AUTH.loginEmail} className="mt-1.5" />
                            </div>
                            <div>
                                <div className="flex items-center justify-between">
                                    <Label htmlFor="password">{t("password")}</Label>
                                    <Link
                                        to="/forgot-password"
                                        data-testid="forgot-password-link"
                                        className="text-xs text-primary hover:underline"
                                    >
                                        {t("login_forgot")}
                                    </Link>
                                </div>
                                <Input id="password" type="password" required value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    data-testid={AUTH.loginPassword} className="mt-1.5" />
                            </div>
                            <Button type="submit" disabled={loading}
                                data-testid={AUTH.loginSubmit}
                                className="w-full bg-primary hover:bg-primary/90 h-11">
                                {loading ? t("please_wait") : t("login_submit")}
                            </Button>
                            <p className="text-sm text-center text-brand-stone">
                                {t("no_account")} <Link to="/signup" className="text-primary font-medium hover:underline">{t("signup")}</Link>
                            </p>
                        </form>
                        ) : (
                        <form onSubmit={onVerify} className="space-y-5" data-testid="mfa-step">
                            <div className="mb-4">
                                <div className="overline text-brand-stone">{t("twofa_mfa_kicker")}</div>
                                <h2 className="font-serif text-2xl mt-1 text-brand-ink">{t("twofa_mfa_title")}</h2>
                                <p className="text-sm text-brand-stone mt-2">{t("twofa_mfa_body")}</p>
                            </div>
                            <div>
                                <Label htmlFor="mfa-code">{t("twofa_verification_code")}</Label>
                                <Input
                                    id="mfa-code"
                                    autoFocus
                                    required
                                    value={code}
                                    onChange={(e) => setCode(e.target.value)}
                                    data-testid="mfa-code-input"
                                    placeholder="123 456"
                                    className="mt-1.5 tracking-widest text-center text-lg"
                                />
                            </div>
                            <Button type="submit" disabled={loading}
                                data-testid="mfa-verify-btn"
                                className="w-full bg-primary hover:bg-primary/90 h-11">
                                {loading ? t("please_wait") : t("twofa_verify_signin")}
                            </Button>
                            <button type="button" onClick={() => { setChallenge(null); setCode(""); }} className="w-full text-xs text-brand-stone hover:underline">
                                {t("twofa_use_different")}
                            </button>
                        </form>
                        )}
                    </CardContent>
                </Card>
            </div>
        </main>
    );
}
